import React, { useEffect, useRef, useState } from "react";
import { Button } from "@penmate/react-shared/ui/button";
import { US_STATES } from "@penmate/react-shared/lib/usStates";
import type { Facility } from "@penmate/react-shared/api/facilities";
import type { Candidate, SearchAttempt } from "../api/searches";
import type { SearchEvent, WaitOutcome } from "../PmReactSearchApp";
import { MatchCard } from "./MatchCard";
import { Label, TextInput } from "./fields";
import { useSearchPoll, type ReleaseReason } from "./useSearchPoll";
import { DEFAULT_WAIT_TUNING } from "./waitTuning";
import type { SearchDraft } from "./types";

const stateName = (abbrev?: string): string => {
  if (!abbrev) return "state";
  const found = US_STATES.find((s) => s.abbrev === abbrev);
  return found ? found.name : abbrev;
};

// What one search's wait remembers across visits to this step.
export type WaitMemory = {
  declined: number[];
  // Per attempt: candidate indexes rejected so far (short of the whole attempt).
  rejected: { [attemptId: number]: number[] };
  presented: number[];
  confirmed: number[];
  resolved: boolean;
  mountedAt: number | null;
  // The reason this search's wait released, if it did — a release is one-way
  // for the search, so a revisit must not revive the spinner.
  releaseReason: ReleaseReason;
};
export const newWaitMemory = (): WaitMemory => ({
  declined: [],
  rejected: {},
  presented: [],
  confirmed: [],
  resolved: false,
  mountedAt: null,
  releaseReason: null,
});

export const sourceLabel = (source: string, draft: SearchDraft): string => {
  if (source === "bop") return "Federal inmate locator";
  if (source === "vine") return "Location verification";
  // State sources come and go by slug (stub, tdcj, idoc, kool, …) — never show
  // a raw slug; a state-mode search always reads as "{State} records".
  if (draft.mode === "state") return `${stateName(draft.state)} records`;
  return source;
};

// Only rows with something confident to say are rendered: a source's
// candidates, or work still underway. A source that finished with nothing —
// no matter why — is never commented on: empty, error, and unsupported are
// indistinguishable here by design, and the truthful way to treat them
// identically is silence, not a shared label.
const isRowWorthShowing = (attempt: SearchAttempt): boolean =>
  (attempt.status === "matches" && attempt.candidates.length > 0) ||
  attempt.status === "pending" ||
  attempt.status === "running";

const attemptStatusLine = (attempt: SearchAttempt, declined: boolean): string => {
  if (attempt.status === "matches") {
    const n = attempt.candidates.length;
    // Once the user has said "not them" to this source's candidates, its
    // row must not keep advertising a live match.
    if (declined) return `${n} ${n === 1 ? "match" : "matches"} reviewed`;
    return `${n} possible ${n === 1 ? "match" : "matches"}`;
  }
  return "searching…";
};


export const WaitStep = ({
  searchId,
  draft,
  knownFacility,
  searchFailed,
  addPending,
  confirmFailed,
  keepFailed,
  onEvent,
  onHeroRestChange,
  memory,
  onConfirmMatch,
  onKeepAdding,
  onEscape,
  onRetrySearch,
  lateBirthdate,
  onLateBirthdateChange,
}: {
  searchId: number | null;
  draft: SearchDraft;
  knownFacility: Facility | null;
  searchFailed: boolean;
  addPending: null | "confirm" | "keep";
  confirmFailed: boolean;
  keepFailed: boolean;
  onEvent?: (event: SearchEvent) => void;
  // Tells the host step when the hero plane should rest: the search has
  // finished (or never started), or a match is up for a decision.
  onHeroRestChange?: (rest: boolean) => void;
  // Per-search memory owned by the parent, so Back/forward (which remounts
  // this step) doesn't re-present a rejected candidate or re-emit events.
  memory: WaitMemory;
  onConfirmMatch: (candidate: Candidate, source: string) => void;
  onKeepAdding: () => void;
  onEscape: () => void;
  // The settled surface's optional birthday ask, owned by the parent so a
  // retry's remount or a Back excursion can't destroy what was typed.
  lateBirthdate?: string;
  onLateBirthdateChange?: (value: string) => void;
  // Re-fires the search after a failed create.
  onRetrySearch?: () => void;
}) => {
  // The wait's clocks belong to the search, not to this mount: a Back/forward
  // revisit picks up where the hold left off instead of holding again.
  const mountedAt = useRef(memory.mountedAt || Date.now());
  memory.mountedAt = mountedAt.current;
  const tuning = DEFAULT_WAIT_TUNING;
  const { phase, search, matchAttempt, releaseReason, minWaitPassed, searchDone } = useSearchPoll(
    searchId,
    mountedAt.current,
    tuning,
    memory.releaseReason,
  );
  if (releaseReason) memory.releaseReason = releaseReason;
  // The minimum confidence wait is universal — a create that failed instantly
  // still holds the generic searching surface first, so the release never
  // reads as "no work happened".
  const failedSettled = searchFailed && minWaitPassed;
  // Declines are per attempt: a later match from a *different* source is a new
  // person and must still be shown, so selection skips declined attempts.
  const [declinedIds, setDeclinedIdsState] = useState<number[]>(memory.declined);
  const setDeclinedIds = (ids: number[]) => {
    memory.declined = ids;
    setDeclinedIdsState(ids);
  };

  // Whether the ask is expanded is cosmetic and local; a remount with a
  // typed value reopens the field so the value stays visible.
  const [showBirthdate, setShowBirthdate] = useState(!!lateBirthdate);

  const emit = (event: SearchEvent) => {
    if (!onEvent) return;
    try {
      onEvent(event);
    } catch {
      // ignore
    }
  };

  let displayAttempt: SearchAttempt | null = null;
  if (search) {
    for (const attempt of search.attempts) {
      if (
        attempt.status === "matches" &&
        attempt.candidates.length > 0 &&
        declinedIds.indexOf(attempt.id) === -1
      ) {
        displayAttempt = attempt;
        break;
      }
    }
  }

  const declined = !!matchAttempt && !displayAttempt;
  // A late match must not swap the surface under an in-flight keep-adding —
  // the user would land on the handoff having never seen the match.
  const showingMatch = phase === "match" && !!displayAttempt && addPending !== "keep";
  const settled =
    phase === "released" || phase === "notFoundYet" || declined || failedSettled;
  // Hooks stay above the match-card early return.
  // The plane rests when the search has finished (keyed on the search's own
  // terminal status — `phase` stays "match" after a declined match), when it
  // never started, and while a match is up for a decision.
  const rest = searchDone || failedSettled || showingMatch;
  useEffect(() => {
    if (onHeroRestChange) onHeroRestChange(rest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest]);

  const attempts = search ? search.attempts : [];
  const visibleAttempts = attempts.filter(isRowWorthShowing);
  // Once a settled wait has shown itself with nothing to list, the records
  // section stays gone for the visit — a late row must not push the continue
  // button around under the user's thumb.
  const recordsDismissed = useRef(false);
  if (settled && visibleAttempts.length === 0) recordsDismissed.current = true;

  // One presentation event per attempt, however many polls re-render it —
  // and however many times this step is revisited (the ids live in `memory`).
  const presentedId = showingMatch && displayAttempt ? displayAttempt.id : null;
  useEffect(() => {
    if (presentedId == null || !displayAttempt) return;
    if (memory.presented.indexOf(presentedId) !== -1) return;
    memory.presented.push(presentedId);
    const top = displayAttempt.candidates[0];
    emit({
      type: "search-match-presented",
      search_id: searchId,
      source: displayAttempt.source,
      facility_resolved: displayAttempt.resolved_facility_address_id != null,
      facility_named: displayAttempt.candidates.some((c) => !!c.facility),
      facility_conflict: !!(
        knownFacility &&
        knownFacility.name &&
        top &&
        top.facility &&
        top.facility.trim().toLowerCase() !== knownFacility.name.trim().toLowerCase()
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentedId]);

  // The wait resolves once per visit, at the user's first exit action.
  const resolveWait = (outcome: WaitOutcome, escaped: boolean) => {
    if (memory.resolved) return;
    memory.resolved = true;
    const waited = Date.now() - mountedAt.current;
    const seen: string[] = [];
    for (const a of attempts) {
      if (seen.indexOf(a.source) === -1) seen.push(a.source);
    }
    emit({
      type: "search-wait-resolved",
      search_id: searchId,
      waited_ms: waited,
      cap_hit: waited >= tuning.inProgressCapMs,
      // Why the wait had released by this exit, if it had — it persists onto
      // a later in-place match exit; null when the wait never released.
      release_reason: releaseReason,
      outcome,
      branch: knownFacility ? "known_facility" : "unknown",
      federal: draft.mode === "federal",
      sources_seen: seen,
      search_failed: searchFailed,
      escaped,
    });
  };

  const settledOutcome = (): WaitOutcome => {
    if (declined) return "proceeded_after_decline";
    if (phase === "notFoundYet") return "proceeded_no_match";
    return "proceeded_still_searching";
  };

  const headline = declined
    ? `Got it, we'll keep checking`
    : failedSettled
      ? `We'll confirm ${draft.firstName}'s details`
      : settled
        ? `We're verifying ${draft.firstName}'s details`
        : `We're looking for ${draft.firstName} ${draft.lastName}`;

  // Persist across the wait→match subtree swap so assistive tech hears the
  // transition (a freshly-inserted live region is often not announced), with
  // a distinct line per settled state so a newly available exit is announced.
  const status = showingMatch
    ? `We found a match. Is this ${draft.firstName}?`
    : failedSettled
      ? "Search queued for our team. You can continue now."
      : declined
        ? "Noted. Still searching."
        : settled
            ? "Still searching. You can continue now."
            : "Searching";
  const announcer = (
    <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {status}
    </span>
  );

  if (showingMatch && displayAttempt) {
    const attempt = displayAttempt;
    return (
      <>
        {announcer}
        <MatchCard
          key={attempt.id}
          firstName={draft.firstName}
          candidates={attempt.candidates}
          sourceLabel={sourceLabel(attempt.source, draft)}
          onDeclinedAll={() => {
            emit({ type: "search-match-declined", search_id: searchId, source: attempt.source });
            setDeclinedIds(declinedIds.concat(attempt.id));
          }}
          onConfirm={(candidate) => {
            // A retry after a failed add is the same decision — one event.
            if (memory.confirmed.indexOf(attempt.id) === -1) {
              memory.confirmed.push(attempt.id);
              emit({ type: "search-match-confirmed", search_id: searchId, source: attempt.source });
            }
            resolveWait("match_presented", false);
            onConfirmMatch(candidate, attempt.source);
          }}
          confirmPending={addPending === "confirm"}
          confirmFailed={confirmFailed}
          initialRejected={memory.rejected[attempt.id]}
          onReject={(rejected) => {
            memory.rejected[attempt.id] = rejected;
          }}
          onEscape={() => {
            resolveWait("match_presented", true);
            onEscape();
          }}
        />
      </>
    );
  }

  return (
    <>
      {announcer}
      <div className="flex w-full flex-col items-center gap-5 pt-2 text-center">
      <div>
        <h1 className="text-[28px] font-extrabold leading-[1.15] tracking-[-0.02em] text-foreground">
          {headline}
        </h1>
        <p className="mt-2 text-base text-muted-foreground">
          {failedSettled
            ? `Our team will take it from here. You can continue and message ${draft.firstName} now.`
            : declined
              ? "Thanks. We'll keep searching, and our team can dig deeper too."
              : settled
                  ? `We'll finish up in the background. Go ahead and send ${draft.firstName} your first message.`
                  : knownFacility
                    ? `We'll double-check ${knownFacility.name} and other official sources.`
                    : "This usually takes a few seconds. Results will show up right here."}
        </p>
      </div>

      {!failedSettled && !recordsDismissed.current && (visibleAttempts.length > 0 || !settled) && (
        <div className="w-full text-left">
        <p className="mb-2 text-sm font-semibold text-muted-foreground">
          {/* Never past tense: checking continues (our team included) until a
              human settles it — this surface must not sound finished. */}
          Records we&rsquo;re checking
        </p>
        <ul
          className="w-full rounded-2xl border border-border bg-card text-left shadow-sm"
          aria-label="Search progress"
        >
          {visibleAttempts.length === 0 ? (
            <li className="flex min-h-[52px] items-center justify-between px-4 py-3 text-[15px]">
              <span className="font-medium">Official records</span>
              <span className="text-muted-foreground">searching…</span>
            </li>
          ) : (
            visibleAttempts.map((attempt) => (
              <li
                key={attempt.id}
                className="flex min-h-[52px] items-center justify-between border-b border-border px-4 py-3 text-[15px] last:border-b-0"
              >
                <span className="font-medium">{sourceLabel(attempt.source, draft)}</span>
                <span
                  className={
                    attempt.status === "matches" && declinedIds.indexOf(attempt.id) === -1
                      ? "font-semibold text-primary"
                      : "text-muted-foreground"
                  }
                >
                  {attemptStatusLine(attempt, declinedIds.indexOf(attempt.id) !== -1)}
                </span>
              </li>
            ))
          )}
        </ul>
        </div>
      )}

      {settled && (
        <div className="flex w-full flex-col gap-2">
          {!draft.birthdate &&
            (showBirthdate ? (
              <div className="mb-1 w-full text-left">
                <Label htmlFor="pm-wait-dob">{draft.firstName}&rsquo;s birthday (optional)</Label>
                <TextInput
                  id="pm-wait-dob"
                  type="date"
                  min="1900-01-01"
                  max={new Date().toISOString().slice(0, 10)}
                  value={lateBirthdate || ""}
                  onChange={(e) => {
                    if (onLateBirthdateChange) onLateBirthdateChange(e.target.value);
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                className="min-h-[44px] self-center text-[15px] font-semibold text-primary"
                onClick={() => setShowBirthdate(true)}
              >
                Know {draft.firstName}&rsquo;s birthday? It helps us verify
              </button>
            ))}
          {keepFailed && (
            <p className="text-sm font-medium text-destructive" role="alert">
              We couldn&rsquo;t add {draft.firstName} just now. Try again.
            </p>
          )}
          <Button
            size="lg"
            className="h-[52px] w-full rounded-xl text-[17px] font-semibold"
            disabled={addPending !== null}
            onClick={() => {
              resolveWait(settledOutcome(), false);
              onKeepAdding();
            }}
          >
            {addPending ? "Adding…" : keepFailed ? "Try again" : `Continue and message ${draft.firstName}`}
          </Button>
          {keepFailed && (
            <button
              type="button"
              className="min-h-[44px] text-[15px] font-semibold text-primary"
              onClick={() => {
                resolveWait(settledOutcome(), true);
                onEscape();
              }}
            >
              Skip for now and go to My Penmates
            </button>
          )}
          {failedSettled && !keepFailed && onRetrySearch && (
            <button
              type="button"
              className="min-h-[44px] text-[15px] font-semibold text-primary"
              onClick={onRetrySearch}
              disabled={addPending !== null}
            >
              Try the search again
            </button>
          )}
        </div>
      )}
      </div>
    </>
  );
};
